import { useEffect, useMemo, useState } from "react";
import { MapContainer, TileLayer, Marker, CircleMarker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { api } from "@/lib/api";

const BRISBANE = [-27.4698, 153.0251];

const pinIcon = (color, initials, live) => L.divIcon({
  className: "",
  iconSize: [34, 44],
  iconAnchor: [17, 42],
  popupAnchor: [0, -38],
  html: `<div style="position:relative;width:34px;height:44px">
    ${live ? `<span style="position:absolute;left:5px;top:4px;width:24px;height:24px;border-radius:50%;background:${color};opacity:.35;animation:pinPulse 1.8s ease-out infinite"></span>` : ""}
    <svg width="34" height="44" viewBox="0 0 34 44" style="position:absolute;inset:0;filter:drop-shadow(0 3px 4px rgba(0,0,0,.25))">
      <path d="M17 1C8.2 1 1 8 1 16.6 1 28 17 43 17 43s16-15 16-26.4C33 8 25.8 1 17 1z" fill="${color}" stroke="white" stroke-width="2"/>
    </svg>
    <span style="position:absolute;top:8px;left:0;width:34px;text-align:center;color:white;font:600 11px/16px sans-serif">${initials}</span>
  </div>`,
});

const initials = (name) => (name || "?").split(" ").map((p) => p[0]).join("").slice(0, 2).toUpperCase();

function FitBounds({ points }) {
  const map = useMap();
  const key = points.map((p) => p.join(",")).join("|");
  useEffect(() => {
    if (points.length === 1) map.setView(points[0], 13);
    else if (points.length > 1) map.fitBounds(points, { padding: [40, 40], maxZoom: 14 });
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

export function useLiveMap(intervalMs = 30000) {
  const [data, setData] = useState(null);
  useEffect(() => {
    const load = () => api.get("/map/live").then((r) => setData(r.data)).catch(() => {});
    load();
    const t = setInterval(load, intervalMs);
    return () => clearInterval(t);
  }, [intervalMs]);
  return data;
}

export default function LiveMap({ data, height = 520, testid = "live-map" }) {
  const cleaners = (data?.cleaners || []).filter((c) => c.lat != null);
  const bookings = (data?.bookings || []).filter((b) => b.lat != null);
  const points = useMemo(() => [...cleaners, ...bookings].map((p) => [p.lat, p.lng]), [cleaners, bookings]);

  return (
    <div className="rounded-sm overflow-hidden border border-border/60 relative z-0" style={{ height }} data-testid={testid}>
      <MapContainer center={BRISBANE} zoom={11} scrollWheelZoom style={{ height: "100%", width: "100%" }}>
        <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
        <FitBounds points={points} />
        {bookings.map((b) => (
          <CircleMarker key={b.id} center={[b.lat, b.lng]} radius={7}
            pathOptions={{ color: b.color || "#9b8fa0", fillColor: b.color || "#9b8fa0", fillOpacity: 0.25, opacity: 0.5, weight: 2 }}>
            <Popup><strong>{b.time} · {b.client}</strong><br />{b.address}<br /><span style={{ textTransform: "capitalize" }}>{(b.status || "").replace("_", " ")}</span></Popup>
          </CircleMarker>
        ))}
        {cleaners.map((c) => (
          <Marker key={c.id} position={[c.lat, c.lng]} icon={pinIcon(c.color, initials(c.name), c.source === "gps")} zIndexOffset={1000}>
            <Popup>
              <strong>{c.name}</strong><br />{c.status_label}{c.client ? ` · ${c.client}` : ""}
              {c.updated_at && <><br /><small>Updated {new Date(c.updated_at).toLocaleTimeString("en-AU", { hour: "numeric", minute: "2-digit" })}</small></>}
            </Popup>
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
}
